import React, {Fragment, useState} from 'react';
import {Icon, Portal, IconText, Dropdown, Tabs, Button, Popup, FormField, Icons, Flex, DatePicker, Slider} from 'components';
import {noop, over, get, toNumber, partialRight, identity, range, moment, find, toggle, includes, split, pluralize, ordinalizeOrLast, useNamedSelector, useField, useUpdateEffect, useFocusPopup} from 'helpers';
import {WeekDays, StartDate, EndDate, TimesPerDay} from '.';
import {repetitionValues} from 'store/activities';
import css from './repetition.module.scss';

const types = {
  Monthly,
  OneTime,
  PerDay,
  PeriodDays,
  PerWeek,
  Weekly,
  Weekly_Specific: Weekly,
  Yearly,
}

export function Repetition({ allow }) {
  const [ { value }, { error, touched } , { setValue } ] = useField({
    name: 'repetition',
    validate: partialRight(validate, allow),
  });

  const popup = useFocusPopup(RepetitionPopup, {
    allow,
    error,
    setValue,
    value,
    deps: [value, error],
    className: 'adjacent'
  });

  const getRepetition = useNamedSelector('activities.briefRepetition');
  const stringified = (getRepetition(value) || '').replace('Someday', '');
  const showError = error && (touched || error !== true);
  const label = (value.repeatType || !includes(allow, 'onetime')) ? 'Repeat' : (value.ts ? 'Due Date' : 'Due Date / Repeat');

  return (
    <Fragment>
      <FormField Wrapper="div" invalid={error && touched} value={stringified} label={label} {...popup}>
        { showError
          ? <b className={css.error}>{ error } <Icons.Alert /></b>
          : <b>{ stringified }</b>
        }
      </FormField>
      <TimesPerDay allow={allow} />
    </Fragment>
  );
}

export function RepetitionPopup({ allow, popup, value, setValue, error }) {
  popup.setClassName('compact');
  const { today } = useNamedSelector('calendar.days');
  const [ defaultValues ] = useState(repetitionValues);
  const Component = value.isEmpty ? RepetitionTypes : types[value.repeatType || 'OneTime'];

  const setDate = value => setValue({
    ...defaultValues,
    ts: value ? value.unix() : '',
  });

  const setRepeatValues = args => setValue({
    ...value,
    isEmpty: false,
    ts: '',
    scheduleDate: value.scheduleDate || today.unix(),
    ...args,
    status: 'Not Started',
  });

  useUpdateEffect(() => {
    popup.bounce();
  }, [ value.repeatType ]);

  return (
    <div className={css.popup}>
      { includes(allow, 'onetime') &&
        <Tabs>
          <Tabs.Tab icon="activity" onClick={() => setDate(null)} pressed={!value.repeatType}>One Time</Tabs.Tab>
          <Tabs.Tab icon="routine" onClick={() => setRepeatValues(PerDay.defaultValues)} pressed={value.repeatType}>Recurring</Tabs.Tab>
        </Tabs>
      }
      { value.repeatType &&
        <Flex className="middle">
          { includes(allow, 'onetime') && 'Repeat' }
          <Dropdown Popup={RepetitionTypes} inline className="compact" setRepeatValues={setRepeatValues} repeatType={value.repeatType}>
            { get({ PerDay: 'Daily', PerWeek: 'Weekly', Weekly: 'On specific days', Weekly_Specific: 'On specific days', PeriodDays: 'Periodicly' }, value.repeatType) || value.repeatType }
          </Dropdown>
        </Flex>
      }
      <Component popup={popup} {...{ allow, setValue, setDate, setRepeatValues, error, ...value }} />
      { !value.isEmpty && value.repeatType &&
        <Flex className={css('begin-end')}>
          <div>
            <p>Starting:</p>
            <small>
              <Dropdown inline Popup={StartDate} value={value} setRepeatValues={setRepeatValues}>
                { moment.unix(value.scheduleDate).calendar().replace(/(Mon|Tue|Wed|Thu|Fri|Sat|Sun), /, '') }
              </Dropdown>
            </small>
          </div>
          <div>
            <p>Ending:</p>
            <small>
              <Dropdown inline Popup={EndDate} value={value} setRepeatValues={setRepeatValues}>
                { value.endDate ? moment.unix(value.endDate).calendar().replace(/(Mon|Tue|Wed|Thu|Fri|Sat|Sun), /, '') : 'Repeat Forever' }
              </Dropdown>
            </small>
          </div>
        </Flex>
      }
    </div>
  );
}

function RepetitionTypes({ repeatType, setRepeatValues, isEmpty, popup }) {
  return (
    <Popup.Menu onClick={isEmpty ? noop : popup.close}>
      <Popup.Menu.Item pressed={repeatType == 'PerDay'} onClick={() => setRepeatValues(PerDay.defaultValues) || popup.close()}>Daily</Popup.Menu.Item>
      <Popup.Menu.Item pressed={repeatType == 'PerWeek'} onClick={() => setRepeatValues(PerWeek.defaultValues) || popup.close()}>Weekly</Popup.Menu.Item>
      <Popup.Menu.Item pressed={repeatType == 'Monthly'} onClick={() => setRepeatValues(Monthly.defaultValues) || popup.close()}>Monthly</Popup.Menu.Item>
      <Popup.Menu.Item pressed={repeatType == 'Yearly'} onClick={() => setRepeatValues(Yearly.defaultValues) || popup.close()}>Yearly</Popup.Menu.Item>
      <Popup.Menu.Item pressed={repeatType == 'Weekly'} onClick={() => setRepeatValues(Weekly.defaultValues) || popup.close()}>On specific days</Popup.Menu.Item>
      <Popup.Menu.Item pressed={repeatType == 'PeriodDays'} onClick={() => setRepeatValues(PeriodDays.defaultValues) || popup.close()}>Periodicly</Popup.Menu.Item>
    </Popup.Menu>
  );
}

function OneTime({ts, setDate, popup}) {
  const [width, setWidth] = useState();
  const {today, tomorrow} = useNamedSelector('calendar.days');
  const onChange = over(setDate, popup.close);
  return (
    <div ref={(node) => node && setWidth(node.offsetWidth)}>
      <Flex id="one-time-header" className="middle">
        <Button className="tiny" pressed={ts === today.unix()} onClick={() => onChange(today)}>Today</Button>
        <Button className="tiny" pressed={ts === tomorrow.unix()} onClick={() => onChange(tomorrow)}>Tomorrow</Button>
        <Button className={['tiny', 'pull-left']} pressed={!ts} onClick={() => onChange(null)}>Someday</Button>
      </Flex>
      <div className={css('date-picker')}>
        <DatePicker
          monthsShown={1}
          selected={ts ? moment.unix(ts) : undefined}
          onChange={onChange}
          renderCustomHeader={width > 500 ? CustomHeader : undefined}
        />
      </div>
    </div>
  );
}

function CustomHeader({customHeaderCount, prevMonthButtonDisabled, decreaseMonth, nextMonthButtonDisabled, increaseMonth, monthDate, changeMonth, changeYear}) {
  return customHeaderCount === 0 && (
    <Portal node={window.document.getElementById('one-time-header')}>
      <small>
        <Dropdown inline Popup={DatePicker.Months} monthDate={monthDate} changeMonth={changeMonth}>
          { moment(monthDate).format('MMM') }
        </Dropdown>
        {' '}
        <Dropdown inline Popup={DatePicker.Years} monthDate={monthDate} changeYear={changeYear}>
          { moment(monthDate).format('YYYY') }
        </Dropdown>
      </small>
      <Icon name="chevron-left" disabled={prevMonthButtonDisabled} className={css.prev} onClick={decreaseMonth} />
      <Icon name="chevron-right" disabled={nextMonthButtonDisabled} className={css.next} onClick={increaseMonth} style={{marginLeft: '-0.5em'}} />
    </Portal>
  );
}

export function PerDay({ setRepeatValues, repeatDays, error }) {
  const toggleDay = day => setRepeatValues({
    repeatDays: toggle(repeatDays, day.isoWeekday().toString()),
  });
  return (
    <div>
      <IconText icon={error && { name: 'alert', className: 'danger' }}>On the following days:</IconText>
      <WeekDays value={repeatDays} toggleDay={toggleDay} error={error} />
    </div>
  );
}

PerDay.defaultValues = {
  repeatType: 'PerDay',
  repeatCount: 1,
  repeatDays: range(1, 8).join(','),
}

function PerWeek(props) {
  const { repeatCount, setRepeatValues } = props;
  return (
    <div>
      <em>{ repeatCount }</em> { pluralize(' day', repeatCount) } per week
      <Slider className={css.slider} min={1} max={7} value={repeatCount || 1} setValue={repeatCount => setRepeatValues({ repeatCount })} options filled />
    </div>
  );
}

PerWeek.defaultValues = {
  repeatType: 'PerWeek',
  repeatCount: 1,
  repeatCountPerDay: 1,
}

function Monthly({ repeatCount, setRepeatValues, repeatDays, error }) {
  return (
    <Fragment>
      <div>
        <IconText icon={error && { name: 'alert', className: 'danger' }}>On the following days of the month:</IconText>
        <ul className={css.days}>
          { range(1, 32).map(day =>
            <Button
              key={day}
              className="snow"
              pressed={split(repeatDays, ',').indexOf(day+'') != -1}
              onClick={() => setRepeatValues({ repeatDays: toggle(repeatDays || '', day + '', { sort: toNumber }) })}
              children={day}
            />
          ) }
        </ul>
      </div>
      <div disabled={!repeatDays}>
        Every <em>{ repeatCount }</em> { pluralize(' month', repeatCount) }
        <Slider className={css.slider} min={1} max={12} value={repeatCount || 1} setValue={repeatCount => setRepeatValues({ repeatCount })} options />
      </div>
    </Fragment>
  );
}

Monthly.defaultValues = {
  repeatType: 'Monthly',
  repeatDays: moment().date(),
  repeatCount: 1,
  repeatCountPerDay: 1,
}

function PeriodDays(props) {
  const { repeatCount, setRepeatValues } = props;
  return (
    <div>
      Every <em>{ repeatCount }</em> { pluralize(' day', repeatCount) }
      <Slider className={css.slider} min={1} max={10} value={repeatCount || 1} setValue={repeatCount => setRepeatValues({ repeatCount })} options />
    </div>
  );
}

PeriodDays.defaultValues = {
  repeatType: 'PeriodDays',
  repeatCount: 1,
  repeatCountPerDay: 1
}

function Weekly(props) {
  const { repeatType, repeatDays, repeatCount, setRepeatValues } = props;

  const toggleDay = day => setRepeatValues({
    repeatDays: toggle(repeatDays, day.isoWeekday().toString()),
  });

  const type = repeatMonthType(repeatDays);

  return (
    <Fragment>
      { type.name == 'week' &&
        <div>
          <p>On the following days</p>
          <WeekDays value={repeatDays} toggleDay={toggleDay} />
        </div>
      }
      <div>
        <Dropdown inline className="compact" Popup={WeeklyTypePopup} {...{ type, repeatType, repeatCount, repeatDays, setRepeatValues }}>
          { repeatType == 'Weekly' && 'Every '.concat(repeatCount, pluralize(' week', repeatCount)) }
          { repeatType != 'Weekly' &&
            <Fragment>
              { repeatDays
                ? ordinalizeOrLast(repeatCount).concat(' ', type.humanized, ' ', 'of the month')
                : ordinalizeOrLast(repeatCount).concat(' occurance ', 'of the month')
              }
            </Fragment>
          }
        </Dropdown>
        <Slider
          className={css.slider}
          min={1}
          max={6}
          options={{ label: repeatType == 'Weekly' ? identity : ordinalizeOrLast }}
          setValue={repeatCount => setRepeatValues({ repeatCount })}
          value={repeatCount}
        />
      </div>
    </Fragment>
  );
}

Weekly.defaultValues = {
  repeatType: 'Weekly',
  repeatCount: 1,
  repeatCountPerDay: 1,
}

function WeeklyTypePopup({ repeatDays, repeatType, setRepeatValues, popup }) {
  const change = values => {
    setRepeatValues({ ...values, repeatCount: 1 });
    popup.close();
  }
  return (
    <Popup.Menu>
      <Popup.Menu.Item onClick={() => change({ repeatDays: '', repeatType: 'Weekly' })} pressed={repeatType == 'Weekly' && !/[a-z]/.test(repeatDays)}>
        Every X week(s)
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: '', repeatType: 'Weekly_Specific' })} pressed={repeatType == 'Weekly_Specific' && !/[a-z]/.test(repeatDays)}>
        On X occurance of the month
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'day', repeatType: 'Weekly_Specific' })} pressed={repeatDays == 'day'}>
        On X day of the month
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'weekday', repeatType: 'Weekly_Specific' })} pressed={repeatDays == 'weekday'}>
        On X weekday of the month
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'weekend_day', repeatType: 'Weekly_Specific' })} pressed={repeatDays == 'weekend_day'}>
        On X weekend day of the month
      </Popup.Menu.Item>
    </Popup.Menu>
  );
}

function Yearly(props) {
  const { repeatCount, repeatDays, repetitionInterval, setRepeatValues, months } = props;

  const type = repeatMonthType(repeatDays);

  const toggleDay = day => setRepeatValues({
    repeatDays: toggle(repeatDays, day.isoWeekday().toString()),
  });

  const toggleMonth = index => setRepeatValues({
    months: toggle(months, index.toString(), { sort: parseInt }),
  });

  return (
    <Fragment>
      { type.name == 'week' &&
        <div>
          <Flex className="middle">
            Pick days of the week or
            <small>
              <Dropdown inline className="compact" Popup={YearlyTypesPopup} {...{ type, repeatCount, setRepeatValues }}>
                select..
              </Dropdown>
            </small>
          </Flex>
          <WeekDays value={repeatDays} toggleDay={toggleDay} />
        </div>
      }
      { repeatDays &&
        <div>
          { type.name == 'week'
            ? <p>{ ordinalizeOrLast(repeatCount).concat(' ', type.humanized, ' ', 'of the month') }</p>
            : <Dropdown inline className="compact" Popup={YearlyTypesPopup} {...{ type, repeatCount, setRepeatValues }}>
              { ordinalizeOrLast(repeatCount).concat(' ', type.humanized, ' ', 'of the month') }
            </Dropdown>
          }
          <Slider
            className={css.slider}
            max={6}
            min={1}
            options={{ label: ordinalizeOrLast }}
            setValue={repeatCount => setRepeatValues({ repeatCount })}
            value={repeatCount}
          />
        </div>
      }
      <div>
        Months:
        <div className={css.months}>
          { moment.monthsShort().map((month, index) =>
            <Button
              key={month}
              pressed={includes(split(months, ','), (index + 1).toString())}
              onClick={() => toggleMonth(index + 1)}
              error={!months}
              children={month}
              icon="check"
              className="checkbox"
            />
          ) }
        </div>
      </div>
      <div>
        Every <em>{ repetitionInterval } </em> { pluralize('year', repetitionInterval) }
        <Slider className={css.slider} min={1} max={10} value={repetitionInterval} setValue={repetitionInterval => setRepeatValues({ repetitionInterval })} options />
      </div>
    </Fragment>
  );
}

Yearly.defaultValues = {
  repeatType: 'Yearly',
  repeatCount: 1,
  repeatCountPerDay: 1,
  repetitionInterval: 1,
  months: moment().month() + 1,
}

function YearlyTypesPopup({ type, setRepeatValues, popup }) {
  const change = values => {
    setRepeatValues({ ...values, repeatCount: 1 });
    popup.close();
  }
  return (
    <Popup.Menu>
      <Popup.Menu.Item onClick={() => change({ repeatDays: '' })} pressed={type.name == 'week'}>
        X days of the week
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'day' })} pressed={type.name == 'day'}>
        X day of the month
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'weekday' })} pressed={type.name == 'weekday'}>
        X weekday of the month
      </Popup.Menu.Item>
      <Popup.Menu.Item onClick={() => change({ repeatDays: 'weekend_day' })} pressed={type.name == 'weekend_day'}>
        X weekend day of the month
      </Popup.Menu.Item>
    </Popup.Menu>
  );
}

function validate({ repeatType, repeatDays, months, ts }, allow) {
  if (/(Weekly|PerDay)/.test(repeatType) && !repeatDays) {
    return 'Check at least one day..';
  } else if (repeatType == 'Monthly' && !repeatDays) {
    return 'Select at least one day of the month..';
  } else if (repeatType == 'Yearly' && !months) {
    return 'Check at least one month..';
  } else if (!repeatType && !includes(allow, 'onetime')) {
    return true;
  } else if (!repeatType && !ts && !includes(allow, 'someday')) {
    return true;
  }
}

function repeatMonthType(value) {
  const name = find(['day', 'weekday', 'weekend_day'], option => option == value) || 'week';
  const humanized = name.replace(/\bweek\b/, () => {
    const week = ' Mon Tue Wed Thu Fri Sat Sun'.split(' ');
    return split(value, ',').map(index => week[index]).join(', ');
  }).replace('_', ' ');
  return { name, humanized };
}
